import type { TagTone } from '@dayon/design-system';
import { PLAYER_STATUS_TRANSITIONS } from '@dayon/contracts';

/**
 * §8's vocabulary, in one place.
 *
 * The database stores SCREAMING_SNAKE and the design writes sentence case. One
 * mapping, here, so no cell in the roster and no option in a dialog invents its
 * own wording — and so a value the schema cannot hold cannot be offered.
 *
 * Every closed set below MIRRORS a zod enum in packages/contracts. The route is
 * the boundary; these are the options the operator is given. When they drift,
 * the form offers something the route refuses, which the operator reads as a
 * broken button rather than as a rule.
 */

export const TIER_LABEL: Record<string, string> = {
  BRONZE: 'Bronze',
  SILVER: 'Silver',
  GOLD: 'Gold',
  VIP_3: 'VIP 3',
  VIP_4: 'VIP 4',
};

export const KYC_LABEL: Record<string, string> = {
  UNVERIFIED: 'Unverified',
  PENDING: 'Pending',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
};

export const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  FLAGGED: 'Flagged',
  RESTRICTED: 'Restricted',
  SUSPENDED: 'Suspended',
};

export const TX_KIND_LABEL: Record<string, string> = {
  DEPOSIT: 'Deposit',
  WITHDRAWAL: 'Withdrawal',
  BET: 'Bet',
  WIN: 'Win',
  BONUS_GRANT: 'Bonus grant',
  BONUS_CONVERT: 'Bonus converted',
  BONUS_FORFEIT: 'Bonus forfeit',
  ADJUSTMENT: 'Adjustment',
  COMMISSION: 'Commission',
  REVERSAL: 'Reversal',
};

/** Tier: accent-filled for VIP, neutral otherwise. The design also draws a
    "Watch" tier — player.tier has no such value, so it can never render. */
export const tierTone = (tier: string): TagTone =>
  tier.startsWith('VIP') ? 'accent' : 'neutral';

/** Verified neutral, Pending outlined, Rejected and Unverified quiet. */
export const kycTone = (kyc: string): TagTone =>
  kyc === 'VERIFIED' ? 'neutral' : kyc === 'PENDING' ? 'outline' : 'quiet';

/** Active filled, Flagged and Suspended outlined, Restricted neutral. */
export const statusTone = (status: string): TagTone =>
  status === 'ACTIVE' ? 'accent' : status === 'RESTRICTED' ? 'neutral' : 'outline';

/* ── Status chips ──────────────────────────────────────────────────────────
   Mutually exclusive, and exactly the four values player.status can hold. */
export const CHIPS = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'FLAGGED', label: 'Flagged' },
  { key: 'RESTRICTED', label: 'Restricted' },
  { key: 'SUSPENDED', label: 'Suspended' },
] as const;

export type ChipKey = (typeof CHIPS)[number]['key'];
export const isChipKey = (value: string): value is ChipKey => CHIPS.some((c) => c.key === value);

/* ── Sort ──────────────────────────────────────────────────────────────────
   Six keys and two directions, all twelve served by fixed SQL in
   /api/players. The route maps a CLOSED set onto those fragments — caller text
   never reaches an ORDER BY — and every one of them ends in a unique column, so
   paging cannot show a row twice.

   Player and Balance were rendered `disabled` for as long as the route had no
   fragment for them, because sorting the eight rows on screen would have
   reordered one page and quietly misrepresented every other. That constraint is
   gone rather than worked around. */
export const SORTS = [
  { key: 'LAST_SEEN', label: 'Last seen' },
  { key: 'PLAYER', label: 'Player' },
  { key: 'BALANCE', label: 'Balance' },
  { key: 'DEPOSITS', label: 'Lifetime deposits' },
  { key: 'GGR', label: 'Lifetime GGR' },
  { key: 'REGISTERED', label: 'Registered' },
] as const;

export type SortKey = (typeof SORTS)[number]['key'];
export const isSortKey = (value: string): value is SortKey => SORTS.some((s) => s.key === value);

export type SortDirection = 'ASC' | 'DESC';
export const isSortDirection = (value: string): value is SortDirection =>
  value === 'ASC' || value === 'DESC';

/**
 * What each direction MEANS for each key, in words.
 *
 * "Ascending" is not a sentence an operator reads a roster with — "oldest
 * first" and "smallest balance first" are. The arrow alone carries a direction;
 * this carries the meaning, and it is what the header hint prints.
 */
export const SORT_SENSE: Record<SortKey, { DESC: string; ASC: string }> = {
  LAST_SEEN: { DESC: 'most recently seen first', ASC: 'longest dormant first' },
  PLAYER: { DESC: 'Z to A by display name', ASC: 'A to Z by display name' },
  BALANCE: { DESC: 'largest cash balance first', ASC: 'smallest cash balance first' },
  DEPOSITS: { DESC: 'largest lifetime deposits first', ASC: 'smallest lifetime deposits first' },
  GGR: { DESC: 'largest lifetime GGR first', ASC: 'smallest lifetime GGR first' },
  REGISTERED: { DESC: 'newest registration first', ASC: 'oldest registration first' },
};

/* ── The account controls a staff member may apply ─────────────────────────
   Four of the design's five. Self-exclusion is missing on purpose and the
   dialog says why: a staff-set self-exclusion would be a staff-LIFTABLE one,
   because the database only protects a row the PLAYER set. A control that
   reads as a full account lock and is not one is worse than no control. */
export const RESTRICTION_KINDS = [
  {
    kind: 'WITHDRAWAL_BLOCK',
    label: 'Withdrawal block',
    effect: 'Blocks new withdrawal requests; requests already in the queue continue.',
  },
  {
    kind: 'DEPOSIT_LIMIT',
    label: 'Deposit limit',
    effect: 'Caps what the account may deposit. The cap is stored as an amount, with no period.',
  },
  {
    kind: 'BONUS_EXCLUSION',
    label: 'Bonus exclusion',
    effect: 'The player cannot opt into any promotion.',
  },
  {
    kind: 'LIVE_RESTRICTED',
    label: 'Live casino restricted',
    effect: 'Blocks Evolution and the in-house live tables.',
  },
] as const;

export type RestrictionKindKey = (typeof RESTRICTION_KINDS)[number]['kind'];

/* The code is what is stored on the restriction row and what a compliance
   report groups by; the note the operator writes travels with the audit entry,
   where prose belongs. */
export const RESTRICTION_REASONS = [
  { code: 'RISK_REVIEW', label: 'Risk review' },
  { code: 'FRAUD_INVESTIGATION', label: 'Fraud investigation' },
  { code: 'KYC_INCOMPLETE', label: 'KYC incomplete' },
  { code: 'CHARGEBACK', label: 'Chargeback' },
  { code: 'RESPONSIBLE_GAMING', label: 'Responsible gaming' },
  { code: 'PLAYER_REQUEST', label: 'Player request' },
  { code: 'REGULATORY_ORDER', label: 'Regulatory order' },
] as const;

/* ── Status moves ──────────────────────────────────────────────────────────
   Three targets, each with its own vocabulary of reasons, because they are
   three different decisions. A suspension refused for "flagged in error" would
   be a code a compliance report cannot read.

   RESTRICTED is deliberately not among them: that status is owned by
   player.player_restriction, so it is set by applying a control and given back
   by lifting one.

   EACH MOVE CARRIES ITS TONE, by the contract beside `.notice-warn` in the
   design system: warn is a reversible consequence, danger the irreversible.
   A suspension closes the account and ends every session at once
   (players/routes.ts), and reinstating lifts the bar but gives no session
   back — danger, as ending every session is on the Security tab. A
   reinstatement hands the account back and is undone by suspending again —
   warn, as lifting a control is. A flag blocks nothing — quiet. */
export const STATUS_MOVES = [
  {
    status: 'SUSPENDED',
    label: 'Suspend',
    /**
     * The PAST TENSE, written out rather than derived.
     *
     * The toast built it as `label + 'd'`, which is right for exactly one of
     * these three: "Reinstate" becomes "Reinstated", and the other two became
     * "Suspendd" and "Flagd". An operator suspending two players was told
     * "Suspendd 2 players". English past tense is not a suffix, and a fourth
     * move added later would have had the same one-in-three chance.
     */
    past: 'Suspended',
    /** What the account can be in for this move to be offered — the SAME
        constant the route refuses by, so the console cannot draw a button
        whose only outcome is a 409. */
    from: PLAYER_STATUS_TRANSITIONS.SUSPENDED,
    tone: 'danger',
    effect:
      'Closes the account: the player can no longer sign in or use it, and every session they ' +
      'have open ends now. Reinstating lifts that bar but brings no session back. The ' +
      'account reads Suspended, and its status history records who suspended it and why. ' +
      'An affiliate’s sign-up bonus for this player that is not yet released waits while they ' +
      'are suspended.',
    reasons: [
      { code: 'FRAUD_CONFIRMED', label: 'Fraud confirmed' },
      { code: 'CHARGEBACK_ABUSE', label: 'Chargeback abuse' },
      { code: 'BONUS_ABUSE', label: 'Bonus abuse' },
      { code: 'REGULATORY_ORDER', label: 'Regulatory order' },
      { code: 'AGE_VERIFICATION_FAILED', label: 'Age verification failed' },
      { code: 'DUPLICATE_ACCOUNT', label: 'Duplicate account' },
      { code: 'PLAYER_REQUEST', label: 'Player request' },
    ],
  },
  {
    status: 'FLAGGED',
    label: 'Flag',
    past: 'Flagged',
    from: PLAYER_STATUS_TRANSITIONS.FLAGGED,
    tone: 'quiet',
    effect:
      'Marks the account for review. Nothing in the platform is blocked by a flag — it puts ' +
      'the player in front of the next reviewer and nothing more.',
    reasons: [
      { code: 'RISK_REVIEW', label: 'Risk review' },
      { code: 'UNUSUAL_DEPOSIT_PATTERN', label: 'Unusual deposit pattern' },
      { code: 'PAYOUT_RATIO', label: 'Payout ratio' },
      { code: 'SHARED_INSTRUMENT', label: 'Shared payment instrument' },
      { code: 'SUPPORT_ESCALATION', label: 'Support escalation' },
      { code: 'KYC_MISMATCH', label: 'KYC mismatch' },
    ],
  },
  {
    status: 'ACTIVE',
    label: 'Reinstate',
    past: 'Reinstated',
    from: PLAYER_STATUS_TRANSITIONS.ACTIVE,
    tone: 'warn',
    effect:
      'Takes the bar or the flag off. A player who still has a control in force lands on ' +
      'Restricted rather than Active, so the roster and the payout rail keep agreeing.',
    reasons: [
      { code: 'INVESTIGATION_CLEARED', label: 'Investigation cleared' },
      { code: 'SUSPENDED_IN_ERROR', label: 'Suspended in error' },
      { code: 'FLAGGED_IN_ERROR', label: 'Flagged in error' },
      { code: 'DOCUMENTS_RECEIVED', label: 'Documents received' },
      { code: 'REGULATORY_ORDER_LIFTED', label: 'Regulatory order lifted' },
      { code: 'PLAYER_APPEAL_UPHELD', label: 'Player appeal upheld' },
    ],
  },
] as const;

export type StatusMoveTarget = (typeof STATUS_MOVES)[number]['status'];

export const statusMove = (target: StatusMoveTarget) =>
  STATUS_MOVES.find((m) => m.status === target) ?? STATUS_MOVES[0];

/* ── Tier ─────────────────────────────────────────────────────────────────
   A tier is a COMMERCIAL decision — it drives the VIP programme and the
   commission a partner earns on that player — so the reasons are commercial
   ones rather than the risk vocabulary a restriction carries. */
export const TIERS = ['BRONZE', 'SILVER', 'GOLD', 'VIP_3', 'VIP_4'] as const;

export const TIER_REASONS = [
  { code: 'VIP_PROGRAMME_REVIEW', label: 'VIP programme review' },
  { code: 'LIFETIME_VALUE_THRESHOLD', label: 'Lifetime value threshold' },
  { code: 'RETENTION_OFFER', label: 'Retention offer' },
  { code: 'DOWNGRADE_INACTIVITY', label: 'Downgrade — inactivity' },
  { code: 'RESPONSIBLE_GAMING', label: 'Responsible gaming' },
  { code: 'APPLIED_IN_ERROR', label: 'Applied in error' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
] as const;
